import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, TrendingUp, BarChart3, Heart, Brain, Sparkles, Users, Waves, Filter, Eye, CalendarDays } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { format, subDays, parseISO, isAfter, isBefore } from 'date-fns';

interface CheckInEntry {
  id: string;
  date: string;
  energy_field?: string;
  body_feeling?: string;
  tension_location?: string;
  emotion?: string;
  mind_state?: string;
  spiritual_connection?: string;
  relational_feeling?: string;
  soul_alignment?: string;
  manifestation_focus?: string;
  manifestation_custom?: string;
  regulation_ritual?: string;
  intuitive_nudge?: string;
}

interface DailyCheckInHistoryProps {
  onStartNewCheckIn?: () => void;
}

export default function DailyCheckInHistory({ onStartNewCheckIn }: DailyCheckInHistoryProps) {
  const [entries, setEntries] = useState<CheckInEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30'); // days
  const [selectedEntry, setSelectedEntry] = useState<CheckInEntry | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadEntries();
  }, [timeRange]);

  const loadEntries = async () => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const daysAgo = new Date();
      daysAgo.setDate(daysAgo.getDate() - parseInt(timeRange));
      
      const { data, error } = await supabase
        .from('daily_checkins')
        .select('*')
        .eq('user_id', user.id)
        .gte('date', daysAgo.toISOString().split('T')[0])
        .order('date', { ascending: false });

      if (error) throw error;
      
      setEntries(data || []);
    } catch (error) {
      toast({
        title: "Error loading history",
        description: "Could not load your check-in history.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getFrequencyAnalysis = (field: keyof CheckInEntry) => {
    const values = entries
      .map(entry => entry[field])
      .filter(Boolean) as string[];
    
    const frequency: Record<string, number> = {};
    values.forEach(value => {
      frequency[value] = (frequency[value] || 0) + 1;
    });
    
    return Object.entries(frequency)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3);
  };

  const getTrendData = () => {
    const trends = {
      energy: getFrequencyAnalysis('energy_field'),
      emotion: getFrequencyAnalysis('emotion'),
      mind: getFrequencyAnalysis('mind_state'),
      spirit: getFrequencyAnalysis('spiritual_connection'),
      soul: getFrequencyAnalysis('soul_alignment')
    };
    
    return trends;
  };

  const formatFieldValue = (value: string) => {
    return value.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());
  };

  const getFieldIcon = (field: string) => {
    switch (field) {
      case 'energy_field': return <Waves className="w-4 h-4" />;
      case 'emotion': return <Heart className="w-4 h-4" />;
      case 'mind_state': return <Brain className="w-4 h-4" />;
      case 'spiritual_connection': return <Sparkles className="w-4 h-4" />;
      case 'relational_feeling': return <Users className="w-4 h-4" />;
      default: return <Calendar className="w-4 h-4" />;
    }
  };

  const trends = getTrendData();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <Card className="p-8 text-center">
        <CalendarDays className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-xl font-semibold mb-2">No Check-Ins Yet</h3>
        <p className="text-muted-foreground mb-6">
          Start your wellness journey by completing your first daily check-in.
        </p>
        {onStartNewCheckIn && (
          <Button onClick={onStartNewCheckIn}>
            Start Your First Check-In
          </Button>
        )}
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header & Controls */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Your Wellness Journey</h2>
          <p className="text-muted-foreground">
            Track your patterns and growth over time
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">Last year</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {onStartNewCheckIn && (
            <Button onClick={onStartNewCheckIn}>
              New Check-In
            </Button>
          )}
        </div>
      </div>

      {/* Analytics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              <h3 className="font-semibold">Total Check-Ins</h3>
            </div>
          </div>
          <div className="text-2xl font-bold text-primary">{entries.length}</div>
          <p className="text-sm text-muted-foreground">
            In the last {timeRange} days
          </p>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Waves className="w-5 h-5 text-primary" />
            <h3 className="font-semibold">Top Energy</h3>
          </div>
          <div className="text-lg font-medium">
            {trends.energy[0] ? formatFieldValue(trends.energy[0][0]) : 'No data'}
          </div>
          <p className="text-sm text-muted-foreground">
            {trends.energy[0] && `${trends.energy[0][1]} times`}
          </p>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Heart className="w-5 h-5 text-primary" />
            <h3 className="font-semibold">Top Emotion</h3>
          </div>
          <div className="text-lg font-medium">
            {trends.emotion[0] ? formatFieldValue(trends.emotion[0][0]) : 'No data'}
          </div>
          <p className="text-sm text-muted-foreground">
            {trends.emotion[0] && `${trends.emotion[0][1]} times`}
          </p>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-5 h-5 text-primary" />
            <h3 className="font-semibold">Consistency</h3>
          </div>
          <div className="text-lg font-medium">
            {Math.round((entries.length / parseInt(timeRange)) * 100)}%
          </div>
          <p className="text-sm text-muted-foreground">
            Daily completion rate
          </p>
        </Card>
      </div>

      {/* Trends Analysis */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-primary" />
          Pattern Analysis
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.entries(trends).map(([category, data]) => (
            <div key={category} className="space-y-3">
              <h4 className="font-medium capitalize flex items-center gap-2">
                {getFieldIcon(category)}
                {category === 'spirit' ? 'Spiritual Connection' : category}
              </h4>
              <div className="space-y-2">
                {data.map(([value, count], index) => (
                  <div key={value} className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      {formatFieldValue(value)}
                    </span>
                    <div className="flex items-center gap-2">
                      <div className="w-16 bg-muted rounded-full h-2">
                        <div 
                          className="bg-primary h-2 rounded-full"
                          style={{ width: `${(count / entries.length) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground w-8">
                        {count}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Daily Entries Timeline */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-primary" />
          Daily Entries
        </h3>
        
        <div className="space-y-3">
          {entries.map((entry) => (
            <div 
              key={entry.id} 
              className="border rounded-lg p-4 hover:bg-muted/50 transition-colors cursor-pointer"
              onClick={() => setSelectedEntry(entry)}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-primary" />
                  <span className="font-medium">
                    {format(parseISO(entry.date), 'EEEE, MMMM d, yyyy')}
                  </span>
                </div>
                <Button variant="ghost" size="sm">
                  <Eye className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                {entry.energy_field && (
                  <div className="flex items-center gap-2">
                    <Waves className="w-3 h-3 text-muted-foreground" />
                    <span className="text-muted-foreground">Energy:</span>
                    <span className="font-medium">{formatFieldValue(entry.energy_field)}</span>
                  </div>
                )}
                {entry.emotion && (
                  <div className="flex items-center gap-2">
                    <Heart className="w-3 h-3 text-muted-foreground" />
                    <span className="text-muted-foreground">Emotion:</span>
                    <span className="font-medium">{formatFieldValue(entry.emotion)}</span>
                  </div>
                )}
                {entry.mind_state && (
                  <div className="flex items-center gap-2">
                    <Brain className="w-3 h-3 text-muted-foreground" />
                    <span className="text-muted-foreground">Mind:</span>
                    <span className="font-medium">{formatFieldValue(entry.mind_state)}</span>
                  </div>
                )}
                {entry.soul_alignment && (
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-3 h-3 text-muted-foreground" />
                    <span className="text-muted-foreground">Alignment:</span>
                    <span className="font-medium">{formatFieldValue(entry.soul_alignment)}</span>
                  </div>
                )}
              </div>
              
              {entry.intuitive_nudge && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-sm text-muted-foreground italic">
                    "{entry.intuitive_nudge}"
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Detailed View Modal */}
      {selectedEntry && (
        <div className="fixed inset-0 bg-foreground/80 flex items-center justify-center p-4 z-50">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold">
                  {format(parseISO(selectedEntry.date), 'EEEE, MMMM d, yyyy')}
                </h3>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setSelectedEntry(null)}
                >
                  ✕
                </Button>
              </div>
              
              <div className="space-y-6">
                {/* Core Check-In Fields */}
{[
                  { key: 'energy_field', label: 'Energy Field', icon: <Waves className="w-4 h-4" /> },
                  { key: 'body_feeling', label: 'Body Feeling', icon: <Heart className="w-4 h-4" /> },
                  { key: 'emotion', label: 'Emotion', icon: <Heart className="w-4 h-4" /> },
                  { key: 'mind_state', label: 'Mind State', icon: <Brain className="w-4 h-4" /> },
                  { key: 'spiritual_connection', label: 'Spiritual Connection', icon: <Sparkles className="w-4 h-4" /> },
                  { key: 'relational_feeling', label: 'Relational Feeling', icon: <Users className="w-4 h-4" /> },
                  { key: 'soul_alignment', label: 'Soul Alignment', icon: <Sparkles className="w-4 h-4" /> }
                ].map(({ key, label, icon }) => {
                  const value = selectedEntry[key as keyof CheckInEntry];
                  if (!value) return null;
                  
                  return (
                    <div key={key} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div className="flex items-center gap-2">
                        {icon}
                        <span className="font-medium">{label}</span>
                      </div>
                      <span className="font-semibold">{formatFieldValue(value)}</span>
                    </div>
                  );
                })}
                
                {/* Optional Fields */}
                {selectedEntry.manifestation_focus && (
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <div className="font-medium mb-1">📿 Manifestation Focus</div>
                    <div>{formatFieldValue(selectedEntry.manifestation_focus)}</div>
                  </div>
                )}
                
                {selectedEntry.manifestation_custom && (
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <div className="font-medium mb-1">✨ Custom Manifestation</div>
                    <div>{selectedEntry.manifestation_custom}</div>
                  </div>
                )}
                
                {selectedEntry.regulation_ritual && (
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <div className="font-medium mb-1">🧘‍♀️ Regulation Ritual</div>
                    <div>{formatFieldValue(selectedEntry.regulation_ritual)}</div>
                  </div>
                )}
                
                {selectedEntry.intuitive_nudge && (
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <div className="font-medium mb-1">🕊️ Intuitive Nudge</div>
                    <div className="italic">"{selectedEntry.intuitive_nudge}"</div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}